import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { SHORTCUTS } from "src/hooks/useGlobalShortcuts";
import { MONO_FONT_STACK } from "src/theme/tokens";

// keyboard shortcut cheat sheet.
// Listens for `netbase:openCheatSheet` and renders the SHORTCUTS table from
// `useGlobalShortcuts`. Same dialog is reused for the sidebar "Help" entry.

function isMac() {
  if (typeof navigator === "undefined") return false;
  const ua = (navigator.userAgent || "") + " " + (navigator.platform || "");
  return /Mac|iPhone|iPad|iPod/i.test(ua);
}

function renderKey(label) {
  return (
    <Box
      component="kbd"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 28,
        height: 26,
        px: 0.75,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
        fontFamily: MONO_FONT_STACK,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {label}
    </Box>
  );
}

export default function ShortcutCheatSheet() {
  const [open, setOpen] = useState(false);
  const mac = useMemo(isMac, []);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("netbase:openCheatSheet", onOpen);
    return () => window.removeEventListener("netbase:openCheatSheet", onOpen);
  }, []);

  const handleClose = () => setOpen(false);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Keyboard shortcuts
        <IconButton onClick={handleClose} size="small" aria-label="Close shortcuts dialog">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {SHORTCUTS.map((shortcut) => {
            const keys = mac ? shortcut.keys : shortcut.altKeys;
            // 2-key sequences (e.g. `g r` for navigation) read as
            // "G then R" rather than "G + R".
            const separator = shortcut.sequence ? "then" : "+";
            return (
              <Stack
                key={shortcut.id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography variant="body2">{shortcut.description}</Typography>
                <Stack direction="row" spacing={0.5}>
                  {keys.map((key, index) => (
                    <Box key={`${shortcut.id}-${key}-${index}`} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                      {renderKey(key === "Cmd" ? "\u2318" : key)}
                      {index < keys.length - 1 ? (
                        <Typography variant="caption" color="text.secondary">
                          {separator}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
